import { Candidate, CandidateStep, OnboardingAssignee, OnboardingTemplates } from '@core/models';
import { POSITION_CATALOG } from './os-data';

/**
 * Seed data for the candidate pipeline + editable onboarding templates
 * (session-only in this preview). Steps are assigned to a company position
 * (holder chosen per hire), the direct Supervisor, or the new-hire Employee.
 * Buckets add Month 1 + 90-Day. Everything here is editable under Onboarding →
 * Checklists and drives the candidate → hire → onboarding lifecycle.
 */

/** First catalog position id in a division (for seeding position assignees). */
const posInDiv = (divId: string): string | undefined =>
  (POSITION_CATALOG as { id: string; division?: string }[]).find((p) => p.division === divId)?.id;

/** Map a legacy owner label → an assignee (positions where a division exists, else Supervisor/Employee). */
const asg = (owner: string): OnboardingAssignee => {
  switch (owner) {
    case 'Employee': return { kind: 'employee' };
    case 'Supervisor': return { kind: 'supervisor' };
    case 'HR': return { kind: 'position', positionId: posInDiv('HR') };
    case 'IT': return { kind: 'position', positionId: posInDiv('IT') };
    default: return { kind: 'supervisor' }; // Division Leader / Safety / System → refine in the editor
  }
};

export const DEFAULT_ONBOARDING_TEMPLATES: OnboardingTemplates = {
  stages: [
    {
      id: 'stage-screening',
      label: 'Screening',
      items: [
        { id: 'ph-1', label: 'Resume / application reviewed', assignee: asg('HR') },
        { id: 'ph-2', label: 'Phone screen completed', assignee: asg('HR') },
        { id: 'ph-3', label: 'Skills / trade assessment', assignee: asg('Supervisor') },
        { id: 'ph-4', label: 'Interview scheduled', assignee: asg('HR') },
        { id: 'ph-5', label: 'Interview completed', assignee: asg('Supervisor') },
        { id: 'ph-6', label: 'References checked', assignee: asg('HR') },
        { id: 'ph-9', label: 'Decision reviewed with hiring manager', assignee: asg('Division Leader') },
      ],
    },
    {
      id: 'stage-onboarding',
      label: 'Onboarding',
      items: [
        { id: 'ph-7', label: 'Compensation range confirmed', assignee: asg('HR') },
        { id: 'ph-8', label: 'Background check initiated', assignee: asg('HR') },
        { id: 'ob-1', label: 'Offer letter sent', assignee: asg('HR') },
        { id: 'ob-2', label: 'Offer accepted / signed', assignee: asg('HR') },
      ],
    },
  ],
  buckets: [
    {
      id: 'bkt-before',
      label: 'Before Day 1',
      items: [
        { id: 'b1-e1', label: 'Complete new-hire paperwork (I-9, W-4)', assignee: asg('Employee') },
        { id: 'b1-e2', label: 'Set up direct deposit', assignee: asg('Employee') },
        { id: 'b1-1', label: 'Confirm role expectations', assignee: asg('Supervisor') },
        { id: 'b1-2', label: 'Prepare the first assignment', assignee: asg('Supervisor') },
        { id: 'b1-3', label: 'Schedule the first-day meeting', assignee: asg('Supervisor') },
        { id: 'b1-4', label: 'Assign a mentor / buddy', assignee: asg('Supervisor') },
        { id: 'b1-5', label: 'Confirm tools & equipment', assignee: asg('IT') },
      ],
    },
    {
      id: 'bkt-day1',
      label: 'Day 1',
      items: [
        { id: 'd1-e1', label: 'Complete your profile & emergency contact', assignee: asg('Employee') },
        { id: 'd1-e2', label: 'Watch the orientation video', assignee: asg('Employee') },
        { id: 'd1-1', label: 'Welcome the employee', assignee: asg('Supervisor') },
        { id: 'd1-2', label: 'Explain the role', assignee: asg('Supervisor') },
        { id: 'd1-3', label: 'Explain the scorecard', assignee: asg('Supervisor') },
        { id: 'd1-4', label: 'Review the first-week plan', assignee: asg('Supervisor') },
        { id: 'd1-5', label: 'Set communication expectations', assignee: asg('Supervisor') },
        { id: 'd1-6', label: 'Walk through the chain of command', assignee: asg('Supervisor') },
      ],
    },
    {
      id: 'bkt-week1',
      label: 'Week 1',
      items: [
        { id: 'w1-1', label: 'Daily 10-minute check-in', assignee: asg('Supervisor') },
        { id: 'w1-2', label: 'Review training completion', assignee: asg('Supervisor') },
        { id: 'w1-3', label: 'Observe work quality', assignee: asg('Supervisor') },
        { id: 'w1-4', label: 'Answer questions', assignee: asg('Supervisor') },
        { id: 'w1-5', label: 'Confirm they understand what good looks like', assignee: asg('Supervisor') },
      ],
    },
    {
      id: 'bkt-month1',
      label: 'Month 1',
      items: [
        { id: 'm1-1', label: 'Review performance', assignee: asg('Supervisor') },
        { id: 'm1-2', label: 'Identify gaps', assignee: asg('Supervisor') },
        { id: 'm1-3', label: 'Adjust training', assignee: asg('Supervisor') },
        { id: 'm1-4', label: 'Confirm the career path', assignee: asg('Division Leader') },
      ],
    },
    {
      id: 'bkt-90day',
      label: '90-Day',
      items: [
        { id: 'd90-1', label: '90-day performance review', assignee: asg('Supervisor') },
        { id: 'd90-2', label: 'Confirm scorecard targets', assignee: asg('Supervisor') },
        { id: 'd90-3', label: 'Set next-quarter goals', assignee: asg('Division Leader') },
      ],
    },
  ],
  intakeQuestions: [
    { id: 'iq-exp', prompt: 'Describe your relevant experience.', kind: 'text' },
    { id: 'iq-pref', prompt: 'Preferred work environment', kind: 'select', options: ['Field', 'Office', 'Mix'] },
    {
      id: 'iq-confident',
      prompt: "Areas you're confident in",
      kind: 'multiselect',
      options: ['Blueprints', 'Safety', 'Scheduling', 'Estimating', 'Field ops', 'Software', 'Client comms'],
    },
    {
      id: 'iq-unfamiliar',
      prompt: "Areas you're less familiar with",
      kind: 'multiselect',
      options: ['Blueprints', 'Safety', 'Scheduling', 'Estimating', 'Field ops', 'Software', 'Client comms'],
    },
    { id: 'iq-need', prompt: 'What support do you need to succeed?', kind: 'text' },
  ],
};

/**
 * A candidate's whole checklist off the session templates — every stage's
 * steps, as the server gives a candidate the whole programme on the day they
 * are added. The ids in `done` are signed on all three tiers, because a step is
 * done when its final review is signed. Used for the seed below and for a
 * candidate added in a session with no server.
 */
export function sessionCandidateSteps(templates: OnboardingTemplates, done: readonly string[], at: string): CandidateStep[] {
  return templates.stages.flatMap((st) =>
    st.items.map((it) => {
      const stamp = done.includes(it.id) ? { byCode: null, at } : undefined;
      return { id: it.id, stageId: st.id, label: it.label, done: !!stamp, notApplicable: false, prepared: stamp, reviewed: stamp, final: stamp, notes: '' };
    }),
  );
}

export const SEED_CANDIDATES: Candidate[] = [
  {
    id: 'cand-1',
    name: 'Marcus Reyes',
    email: 'marcus.reyes@example.com',
    phone: '(801) 555-0142',
    title: 'Project Engineer',
    stage: 'interview',
    steps: sessionCandidateSteps(DEFAULT_ONBOARDING_TEMPLATES, ['ph-1', 'ph-2', 'ph-4'], '2026-08-04'),
    decision: { outcome: 'open' },
    notes: 'Strong field background; referred by a superintendent.',
    initials: 'MR',
    createdAt: '2026-08-04',
    roleAssignments: [],
  },
  {
    id: 'cand-2',
    name: 'Danielle Cho',
    email: 'danielle.cho@example.com',
    phone: '(801) 555-0198',
    title: 'Estimator',
    stage: 'screening',
    steps: sessionCandidateSteps(DEFAULT_ONBOARDING_TEMPLATES, ['ph-1'], '2026-08-11'),
    decision: { outcome: 'open' },
    initials: 'DC',
    createdAt: '2026-08-11',
    roleAssignments: [],
  },
  {
    id: 'cand-3',
    name: 'Tyrell Banks',
    title: 'Laborer',
    stage: 'prospect',
    steps: sessionCandidateSteps(DEFAULT_ONBOARDING_TEMPLATES, [], '2026-08-15'),
    decision: { outcome: 'open' },
    initials: 'TB',
    createdAt: '2026-08-15',
    roleAssignments: [],
  },
];
